import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ButtonWithLoader, Text } from '@pbt/pbt-ui-components'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import DialogNames from '~/constants/DialogNames'
import {
  createInventoryOrderItemReceipts,
  getInventoryOrderError,
  getInventoryOrdersIsUpdating,
} from '~/store/duck/inventoryOrders'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import { useConfirmReceiveOverage } from './hooks/useConfirmReceiveOverage'
import { useDraftReceipts } from './hooks/useDraftReceipts'
import {
  type ReceiptFieldGroup,
  useReceiptFields,
} from './hooks/useReceiptFields'
import { OrderItemReceiptsList } from './OrderItemReceiptsList'
import { OrderItemReceiptsPageHeader } from './OrderItemReceiptsPageHeader'
import { OrderItemReceiptsSummary } from './OrderItemReceiptsSummary'

const StyledButtonWithLoader = styled(ButtonWithLoader)({
  width: 114,
})

const getTotalReceiptQuanities = (receipts: ReceiptFieldGroup[]) => {
  const { totalQuantityDamaged, totalQuantityReceived } = receipts.reduce(
    (totals, { fields }) => {
      const quantity = fields.receivedQuantity.value || 0
      if (fields.damaged.value) {
        totals.totalQuantityDamaged += quantity
      } else {
        totals.totalQuantityReceived += quantity
      }
      return totals
    },
    { totalQuantityDamaged: 0, totalQuantityReceived: 0 },
  )

  return { totalQuantityDamaged, totalQuantityReceived }
}

interface OrderItemReceiptsPageProps {
  inventoryOrder: InventoryOrder
  inventoryOrderItem: InventoryOrderItem
}

export const OrderItemReceiptsPage = ({
  inventoryOrder,
  inventoryOrderItem,
}: OrderItemReceiptsPageProps) => {
  const { t } = useTranslation('Common')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const error = useSelector(getInventoryOrderError)
  const { receiptFields } = useReceiptFields(inventoryOrderItem.receipts)
  const {
    draftReceiptFields,
    addDraftReceipt,
    deleteDraftReceipt,
    validateDraftReceipts,
    getDraftReceiptsForSubmission,
  } = useDraftReceipts()
  const confirmReceiveOverage = useConfirmReceiveOverage(inventoryOrderItem)

  const [openConfirmCloseDialog, closeConfirmCloseDialog] = useDialog(
    DialogNames.CONFIRM_CLOSE_DIALOG,
  )

  const { totalQuantityDamaged, totalQuantityReceived } =
    getTotalReceiptQuanities([...receiptFields, ...draftReceiptFields])

  const navigateToOrderItemsPage = () => {
    navigate(`/admin/catalog/inventories/orders/${inventoryOrder.id}`)
  }

  const closeAfterSaving = useCloseAfterCreation(() => {
    if (!error) {
      navigateToOrderItemsPage()
    }
  }, getInventoryOrdersIsUpdating)

  const submitReceipts = () => {
    const receiptsToSave = getDraftReceiptsForSubmission()
    closeAfterSaving()
    dispatch(
      createInventoryOrderItemReceipts(
        inventoryOrder.id,
        inventoryOrderItem.id,
        receiptsToSave,
      ),
    )
  }

  const handleSaveDraftReceipts = () => {
    if (!draftReceiptFields.length || !validateDraftReceipts()) {
      return
    }

    const isReceivingOverage =
      totalQuantityReceived + totalQuantityDamaged >
      inventoryOrderItem.orderedQuantity

    if (isReceivingOverage) {
      confirmReceiveOverage({
        onConfirm: (proceed: boolean) => {
          if (proceed) {
            submitReceipts()
          }
        },
        totalQuantityOrdered: inventoryOrderItem.orderedQuantity,
        totalQuantityReceived: totalQuantityReceived + totalQuantityDamaged,
      })
      return
    }

    submitReceipts()
  }

  const handleCancel = () => {
    if (draftReceiptFields.length > 0) {
      openConfirmCloseDialog({
        // Save
        onOk: () => {
          handleSaveDraftReceipts()
          closeConfirmCloseDialog()
        },
        // Don't Save
        onNotOk: () => {
          navigateToOrderItemsPage()
          closeConfirmCloseDialog()
        },
      })
      return
    }
    navigateToOrderItemsPage()
  }

  useEffect(() => {
    const shouldCreateInitialDraftReceipt = !(
      receiptFields.length || draftReceiptFields.length
    )
    if (shouldCreateInitialDraftReceipt) {
      addDraftReceipt()
    }
  }, [receiptFields, draftReceiptFields])

  const isSaveDisabled = isUpdating || !draftReceiptFields.length

  return (
    <Grid
      container
      direction="column"
      height="100%"
      justifyContent="space-between"
      px={3}
      py={2}
      wrap="nowrap"
    >
      <Grid item xs display="flex" flexDirection="column" gap={3}>
        <OrderItemReceiptsPageHeader
          inventoryOrder={inventoryOrder}
          inventoryOrderItem={inventoryOrderItem}
        />
        <OrderItemReceiptsList
          addDraftReceipt={addDraftReceipt}
          deleteDraftReceipt={deleteDraftReceipt}
          draftReceiptFields={draftReceiptFields}
          inventoryOrderItem={inventoryOrderItem}
          receiptFields={receiptFields}
        />
        <OrderItemReceiptsSummary
          inventoryOrderItem={inventoryOrderItem}
          orderedQuantity={inventoryOrderItem.orderedQuantity}
          totalQuantityDamaged={totalQuantityDamaged}
          totalQuantityReceived={totalQuantityReceived}
        />
        <Grid alignItems="center" display="flex" gap={2} mt={1}>
          <StyledButtonWithLoader
            color="primary"
            disabled={isSaveDisabled}
            onClick={handleSaveDraftReceipts}
          >
            {t('Common:SAVE_ACTION')}
          </StyledButtonWithLoader>
          <Text link variant="body2" onClick={handleCancel}>
            {t('Common:CANCEL_ACTION')}
          </Text>
        </Grid>
      </Grid>
    </Grid>
  )
}
